// @flow strict-local

import { ufologger } from '@atlassian/ufo-experimental';

import { type ResourceEntry } from '../../../../types';
import { getPerformanceObject, getPerformanceObserverObject } from '../../utils/window-helper';

type PerformanceObserverConstructor = new (
	callback: (list: PerformanceObserverEntryList, observer: PerformanceObserver) => void,
) => PerformanceObserver;

export const resourceTimingBuffer = {
	timings: [] as ResourceEntry[],
	maxSize: 1000,
	observer: null as PerformanceObserver | null,
	transformResource: (entry: PerformanceResourceTiming): ResourceEntry => ({
		name: entry.name,
		initiatorType: entry.initiatorType,
		transferSize: entry.transferSize,
		startTime: Math.round(entry.startTime),
		duration: Math.round(entry.duration),
		fetchStart: Math.round(entry.fetchStart || 0),
		workerStart: Math.round(entry.workerStart || 0),
		responseStart: Math.round(entry.responseStart || 0),
	}),
	start: (
		performance: Performance | undefined = getPerformanceObject(),
		PerformanceObserver:
			| PerformanceObserverConstructor
			| undefined = getPerformanceObserverObject(),
	): void => {
		if (!PerformanceObserver || !performance || typeof PerformanceObserver === 'undefined') {
			return;
		}
		if (resourceTimingBuffer.observer) {
			ufologger.warn('Observer is already running');
			return;
		}
		const resources = performance.getEntriesByType?.('resource');
		if (resources) {
			resourceTimingBuffer.timings = resources.map((resource) =>
				resourceTimingBuffer.transformResource(resource as PerformanceResourceTiming),
			);
		}
		resourceTimingBuffer.observer = new PerformanceObserver(resourceTimingBuffer.addTimings);
		resourceTimingBuffer.observer.observe({ entryTypes: ['resource'] });
	},
	stop: (): void => {
		if (resourceTimingBuffer.observer) {
			resourceTimingBuffer.observer.disconnect();
			resourceTimingBuffer.observer = null;
		}
		resourceTimingBuffer.timings = [];
	},
	addTimings: (list: PerformanceObserverEntryList): void => {
		const entries = list
			.getEntries()
			.map((entry: PerformanceEntry) =>
				resourceTimingBuffer.transformResource(entry as PerformanceResourceTiming),
			);
		const overflow = resourceTimingBuffer.timings.length + entries.length;
		if (overflow > resourceTimingBuffer.maxSize) {
			const fieldsToRemove = overflow - resourceTimingBuffer.maxSize;
			resourceTimingBuffer.timings.splice(0, fieldsToRemove);
		}
		resourceTimingBuffer.timings.push(...entries);
	},
};

export const getResourceTimings = (
	startTime: number,
	performance: Performance | undefined = getPerformanceObject(),
): ResourceEntry[] | null => {
	if (!PerformanceObserver || !resourceTimingBuffer.observer) {
		const resources = performance?.getEntriesByType('resource') as PerformanceResourceTiming[];
		if (resources) {
			return resources.filter((timing) => timing.startTime >= startTime);
		}
		return null;
	}
	return resourceTimingBuffer.timings.filter((timing) => timing.startTime >= startTime);
};
